import { useEffect, useRef, useState } from "react";
import useCurrentUser from "@/hooks/useCurrentUser";
import { getBackendUri } from "@/lib/backendSource";
import { sendToRaygun } from "@/lib/raygunUtils";

export type RecordingAudioState = {
  audioUrl: string | null;
  loading: boolean;
  error: string | null;
};

export function recordingAudioPath(callSid: string): string {
  return `/backoffice/twilio_flex_calls/${encodeURIComponent(callSid)}/recording`;
}

function friendlyRecordingError(status: number | null): string {
  if (status === 401) return "Session expired. Refresh the page and try again.";
  return "Recording unavailable right now.";
}

/**
 * Fetches call recording audio through the authenticated Backoffice proxy and
 * exposes a blob URL for a native audio element. Browsers cannot attach the
 * Okta bearer token to `<audio src>` directly, hence fetch-then-blob.
 */
export default function useRecordingAudio(callSid: string, enabled: boolean): RecordingAudioState {
  const { getAccessToken } = useCurrentUser();
  // getAccessToken identity changes every render in Okta/token auth; read it via ref at call time.
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;
  const [state, setState] = useState<RecordingAudioState>({ audioUrl: null, loading: enabled, error: null });

  useEffect(() => {
    if (!enabled) {
      setState({ audioUrl: null, loading: false, error: null });
      return;
    }

    const controller = new AbortController();
    let objectUrl: string | null = null;
    setState({ audioUrl: null, loading: true, error: null });

    (async () => {
      let status: number | null = null;
      try {
        const token = await getAccessTokenRef.current();
        const response = await fetch(`${getBackendUri()}${recordingAudioPath(callSid)}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal
        });
        status = response.status;
        if (!response.ok) {
          throw new Error(`Recording proxy returned HTTP ${response.status}`);
        }
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        if (controller.signal.aborted) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = null;
          return;
        }
        setState({ audioUrl: objectUrl, loading: false, error: null });
      } catch (error) {
        if (controller.signal.aborted) return;
        sendToRaygun(
          new Error(
            `Recording fetch failed for call ${callSid}: ${error instanceof Error ? error.message : "unknown error"}`
          ),
          {
            tags: ["call-topics", "recording"]
          }
        );
        setState({ audioUrl: null, loading: false, error: friendlyRecordingError(status) });
      }
    })();

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [callSid, enabled]);

  return state;
}
